import { createFileRoute, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Bracket Goblin" },
      {
        name: "description",
        content:
          "How Bracket Goblin collects, uses, and protects your personal information.",
      },
      { property: "og:title", content: "Privacy Policy — Bracket Goblin" },
      {
        property: "og:description",
        content:
          "How Bracket Goblin collects, uses, and protects your personal information.",
      },
      { property: "og:url", content: "https://bracketgoblin.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://bracketgoblin.com/privacy" }],
  }),
  component: PrivacyPolicyPage,
});

const EFFECTIVE_DATE = "May 17, 2026";
const CONTACT_EMAIL = "[privacy@bracketgoblin.com]";
const OPERATOR = "bracketgoblin.com";

function PrivacyPolicyPage() {
  const router = useRouter();
  return (
    <main className="min-h-screen bg-background px-4 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) router.history.back();
              else router.navigate({ to: "/" });
            }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back
          </button>
        </div>

        <article className="prose-invert space-y-6 text-foreground">
          <header className="space-y-2">
            <h1 className="font-display text-4xl font-black tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-sm text-muted-foreground">
              Effective date: {EFFECTIVE_DATE}
            </p>
          </header>

          <Section title="1. Who we are">
            <p>
              Bracket Goblin (“Bracket Goblin,” “we,” “us,” or “our”) is a
              bracket pool web app operated by {OPERATOR}. This Privacy Policy
              explains what information we collect when you use the website at
              bracketgoblin.com and related subdomains (the “Service”), how we
              use it, and the rights you have over it.
            </p>
            <p>
              If you have questions about this policy or your data, contact us
              at <span className="font-mono">{CONTACT_EMAIL}</span>.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <p>We collect only what we need to run the Service:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Account information.</strong> When you sign up or are
                invited to a league, we collect your email address, a display
                name, and an encrypted password (or, if you sign in with a
                third-party provider, the basic profile information that
                provider returns).
              </li>
              <li>
                <strong>League activity.</strong> The leagues you belong to,
                your role in those leagues (member, admin), your bracket picks,
                bonus picks, and any related timestamps.
              </li>
              <li>
                <strong>Authentication metadata.</strong> Login timestamps,
                password reset and email confirmation events, and similar
                lifecycle data used to operate and secure your account.
              </li>
              <li>
                <strong>Email delivery metadata.</strong> Limited delivery
                status information (sent, delivered, bounced) for transactional
                emails we send to you, such as invites and password resets.
              </li>
              <li>
                <strong>Technical data.</strong> Standard server logs from our
                hosting provider, including IP address, browser user agent, and
                request timestamps, used for security and debugging.
              </li>
              <li>
                <strong>Bug reports.</strong> When you voluntarily submit a bug
                report (either by clicking “Report a bug” or the “Report this
                bug” button on an error screen), we collect the page URL,
                browser user agent, viewport size, app version, and a short
                window of recent browser console output to help us diagnose the
                issue. Console output is a technical log from your browser
                session and may incidentally include identifiers tied to your
                account; we make reasonable efforts to filter out sensitive
                values such as authentication tokens and email addresses before
                storage, but cannot guarantee complete redaction. Screenshots
                are <strong>never</strong> captured automatically — they are
                included only if you explicitly attach one to your report.
                Bug reports are linked to your account (user ID and email) so we
                can investigate and follow up with you if needed.
              </li>
            </ul>
            <p>
              We do <strong>not</strong> use third-party advertising networks,
              analytics trackers, or social tracking pixels.
            </p>
          </Section>

          <Section title="3. How we use your information">
            <ul className="list-disc space-y-2 pl-6">
              <li>To create and maintain your account and your leagues.</li>
              <li>
                To record and display your bracket picks, scores, and
                leaderboards.
              </li>
              <li>
                To send transactional emails (invites, password resets, email
                confirmations, league notifications).
              </li>
              <li>
                To secure the Service against abuse, fraud, and unauthorized
                access.
              </li>
              <li>To respond to your support requests.</li>
              <li>To comply with legal obligations.</li>
            </ul>
            <p>
              We do not sell your personal information, and we do not share it
              with third parties for their own marketing.
            </p>
          </Section>

          <Section title="4. Legal bases (EU/UK users)">
            <p>
              If you are in the European Economic Area or the United Kingdom,
              we rely on the following legal bases under the GDPR:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Contract:</strong> processing necessary to provide you
                the Service you signed up for.
              </li>
              <li>
                <strong>Legitimate interests:</strong> securing the Service,
                preventing abuse, and improving reliability.
              </li>
              <li>
                <strong>Consent:</strong> where required, e.g. for optional
                communications. You can withdraw consent at any time.
              </li>
              <li>
                <strong>Legal obligation:</strong> where applicable law requires
                us to retain or disclose information.
              </li>
            </ul>
          </Section>

          <Section title="5. Service providers">
            <p>
              We use a small number of vendors to operate the Service. These
              providers process data on our behalf under their own privacy and
              security commitments:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Hosting and database:</strong> our application,
                database, authentication, and file storage are provided through
                Lovable Cloud (which is built on Supabase) and the underlying
                cloud infrastructure they use.
              </li>
              <li>
                <strong>Email delivery:</strong> transactional emails are sent
                through our email delivery provider.
              </li>
            </ul>
            <p>
              We share with these providers only the data they need to deliver
              their services to us.
            </p>
          </Section>

          <Section title="6. Data retention">
            <p>
              We keep your account information and league data for as long as
              your account is active. If you delete your account, we will
              delete or anonymize your personal information within a reasonable
              period, except where we are required to keep it for legal,
              security, or fraud-prevention reasons. Server logs are retained
              for a limited period for security and debugging.
            </p>
          </Section>

          <Section title="7. Your rights">
            <p>
              Depending on where you live, you may have the right to:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Access the personal information we hold about you.</li>
              <li>Correct inaccurate information.</li>
              <li>
                Delete your account and associated personal information
                (subject to limited exceptions).
              </li>
              <li>Export your information in a portable format.</li>
              <li>Object to or restrict certain processing.</li>
              <li>
                Withdraw consent where processing is based on consent.
              </li>
              <li>
                Lodge a complaint with your local data protection authority.
              </li>
            </ul>
            <p>
              To exercise any of these rights, email us at{" "}
              <span className="font-mono">{CONTACT_EMAIL}</span>. We will
              respond within the time period required by applicable law.
            </p>
          </Section>

          <Section title="8. California privacy rights (CCPA/CPRA)">
            <p>
              If you are a California resident, you have the right to know
              what personal information we collect, the right to request
              deletion of your personal information, the right to correct
              inaccurate information, and the right not to be discriminated
              against for exercising these rights.
            </p>
            <p>
              Categories of personal information we collect are described in
              Section 2 above. We do not “sell” or “share” personal
              information as those terms are defined under California law, and
              we do not use or disclose sensitive personal information for
              purposes that require a right to limit under the CPRA.
            </p>
            <p>
              To submit a request, email{" "}
              <span className="font-mono">{CONTACT_EMAIL}</span> from the email
              address associated with your account so we can verify your
              identity. Authorized agents may submit requests on your behalf
              with written proof of authorization.
            </p>
          </Section>

          <Section title="9. Cookies and similar technologies">
            <p>
              We use a small number of strictly necessary cookies and similar
              storage (for example, to keep you signed in). We do not use
              advertising cookies or third-party analytics cookies.
            </p>
          </Section>

          <Section title="10. Security">
            <p>
              We use industry-standard safeguards including encryption in
              transit, hashed passwords, and role-based access controls to
              protect your information. No system is perfectly secure, however,
              and we cannot guarantee the absolute security of your data.
            </p>
          </Section>

          <Section title="11. Children">
            <p>
              The Service is not directed to children under 13 (or under 16 in
              the EEA/UK). We do not knowingly collect personal information
              from children. If you believe a child has provided us personal
              information, contact us and we will delete it.
            </p>
          </Section>

          <Section title="12. International data transfers">
            <p>
              We and our service providers may process your information in
              countries other than your own, including the United States.
              Where required, we rely on appropriate safeguards such as
              standard contractual clauses to protect your information.
            </p>
          </Section>

          <Section title="13. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. If we make
              material changes, we will update the effective date above and,
              where appropriate, notify you through the Service or by email.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>
              Questions, requests, or complaints? Email{" "}
              <span className="font-mono">{CONTACT_EMAIL}</span>.
            </p>
          </Section>
        </article>
      </div>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl font-bold text-foreground">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}
